import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Switch from "@mui/material/Switch";
import { styled } from "@mui/material/styles";

// Custom styled switch to match the blue toggle in the image
const BlueSwitch = styled(Switch)(({ theme }) => ({
    width: 46,
    height: 22,
    padding: 0,
    '& .MuiSwitch-switchBase': {
      padding: 0,
      margin: 1,
      transitionDuration: '300ms',
      '&.Mui-checked': {
        transform: 'translateX(24px)',
        color: '#fff',
        '& + .MuiSwitch-track': {
          backgroundColor: theme.palette.primary.main,
          opacity: 1,
          border: 0,
        },
        '&.Mui-disabled + .MuiSwitch-track': {
          opacity: 0.5,
        },
      },
      '&.Mui-focusVisible .MuiSwitch-thumb': {
        color: '#33cf4d',
        border: '6px solid #fff',
      },
    },
    '& .MuiSwitch-thumb': {
      boxSizing: 'border-box',
      width: 20,
      height: 20,
    },
    '& .MuiSwitch-track': {
      borderRadius: 22 / 2,
      backgroundColor: '#E9E9EA',
      opacity: 1,
      transition: theme.transitions.create(['background-color'], {
        duration: 500,
      }),
    },
  }));

/**
 * ProctorSettings component
 * 
 * @param {Object} props
 * @param {Object} props.values - Object containing boolean values for each setting
 * @param {Function} props.onChange - Function to be called when a setting changes
 * @param {string} props.boxShadow - Box shadow CSS value for the container
 * @param {boolean} props.disabled - Whether all switches should be disabled
 * @returns {JSX.Element}
 */
const ProctorSettings = ({ 
  values = {},
  onChange,
  boxShadow = "0px 2px 8px rgba(0, 0, 0, 0.1)",
  disabled = false
}) => {
  
  const settings = {
    prohibitCopying: values.prohibitCopying ?? true,
    prohibitTabSwitching: values.prohibitTabSwitching ?? true,
    enableImageCapturing: values.enableImageCapturing ?? true
  };
  
  const handleChange = (setting) => (event) => {
    if (onChange) {
      onChange({
        target: {
          name: `proctorSettings.${setting}`,
          value: event.target.checked
        }
      });
    }
  };
  
  return (
    <Box sx={{ 
      width: '100%', 
      maxWidth: 800,
      boxShadow,
      p: boxShadow === 'none' ? 0 : 2,
      borderRadius: 1
    }}>
      <Typography variant="h6-semiBold" color='primary.main'>
        Proctorship settings
      </Typography>
      
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 , mt : 2}}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="body01-medium" sx={{ fontWeight: 'medium' }}>
            Prohibit students from copying and pasting content
          </Typography>
          <BlueSwitch 
            checked={settings.prohibitCopying} 
            onChange={handleChange('prohibitCopying')} 
            disabled={disabled}
            name="proctorSettings.prohibitCopying"
          />
        </Box>
        
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="body01-medium" sx={{ fontWeight: 'medium' }}>
            Prohibit students from tab-switching
          </Typography>
          <BlueSwitch 
            checked={settings.prohibitTabSwitching} 
            onChange={handleChange('prohibitTabSwitching')} 
            disabled={disabled}
            name="proctorSettings.prohibitTabSwitching"
          />
        </Box>
        
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="body01-medium" sx={{ fontWeight: 'medium' }}>
            Enable students image capturing
          </Typography>
          <BlueSwitch 
            checked={settings.enableImageCapturing} 
            onChange={handleChange('enableImageCapturing')} 
            disabled={disabled}
            name="proctorSettings.enableImageCapturing"
          />
        </Box>
      </Box>
    </Box>
  );
};

export default ProctorSettings;